import {
  applyMiddleware,
  createStore,
  combineReducers
} from "redux";
import thunk from "redux-thunk";
import { composeWithDevToolsDevelopmentOnly } from "@redux-devtools/extension";
import { serializeState } from "../utils/serializeState";
import * as APIResources from "./modules/api-resources";
import * as LocalizedResourcesMap from "./modules/localized-resources-map";
import { ActionTypes, apiResourceFulfilled, setLocalizedResourceId } from "./actions";
import {
  APIResourceType
} from "../api";
import { MAKESWIFT_CACHE_TAG } from "../next/api-handler/handlers/webhook/site-published";
const reducer = combineReducers({
  apiResources: APIResources.reducer,
  localizedResourcesMap: LocalizedResourcesMap.reducer
});
function getLocalizedResourceId(state, locale, resourceId) {
  return LocalizedResourcesMap.getLocalizedResourceId(
    state.localizedResourcesMap,
    locale,
    resourceId
  );
}
function getHasAPIResource(state, resourceType, resourceId, locale) {
  switch (resourceType) {
    case APIResourceType.LocalizedGlobalElement:
      if (locale == null) {
        console.error(`Attempt to access ${resourceType} ${resourceId} without a locale`);
        return false;
      }
      const localizedId = getLocalizedResourceId(state, locale, resourceId);
      return localizedId != null && APIResources.getHasAPIResource(state.apiResources, resourceType, localizedId, locale);
    default:
      return APIResources.getHasAPIResource(state.apiResources, resourceType, resourceId, locale);
  }
}
function getAPIResource(state, resourceType, resourceId, locale) {
  switch (resourceType) {
    case APIResourceType.LocalizedGlobalElement:
      if (locale == null) {
        console.error(`Attempt to access ${resourceType} ${resourceId} without a locale`);
        return null;
      }
      const localizedId = getLocalizedResourceId(state, locale, resourceId);
      return localizedId != null ? APIResources.getAPIResource(state.apiResources, resourceType, localizedId, locale) : null;
    default:
      return APIResources.getAPIResource(state.apiResources, resourceType, resourceId, locale);
  }
}
async function fetchJson(url) {
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    next: { tags: [MAKESWIFT_CACHE_TAG] }
  });
  if (response.status === 404)
    return null;
  if (!response.ok)
    throw new Error(response.statusText);
  if (response.headers.get("content-type")?.includes("application/json") !== true) {
    throw new Error(
      `Expected JSON response from "${url}" but got "${response.headers.get("content-type")}"`
    );
  }
  return response.json();
}
function fetchAPIResource(resourceType, resourceId, locale) {
  return async (dispatch, getState) => {
    const state = getState();
    if (getHasAPIResource(state, resourceType, resourceId, locale)) {
      return getAPIResource(state, resourceType, resourceId, locale);
    }
    let resource;
    switch (resourceType) {
      case APIResourceType.Swatch:
        resource = await fetchJson(`/api/makeswift/swatches/${resourceId}`);
        break;
      case APIResourceType.File:
        resource = await fetchJson(`/api/makeswift/files/${resourceId}`);
        break;
      case APIResourceType.Typography:
        resource = await fetchJson(`/api/makeswift/typographies/${resourceId}`);
        break;
      case APIResourceType.GlobalElement:
        resource = await fetchJson(`/api/makeswift/global-elements/${resourceId}`);
        break;
      case APIResourceType.LocalizedGlobalElement: {
        if (locale == null)
          throw new Error("Locale is required to fetch LocalizedGlobalElement");
        if (getLocalizedResourceId(state, locale, resourceId) === null) {
          return null;
        }
        resource = await fetchJson(
          `/api/makeswift/localized-global-elements/${resourceId}/${locale}`
        );
        dispatch(
          setLocalizedResourceId({
            locale,
            resourceId,
            localizedResourceId: resource?.id ?? null
          })
        );
        break;
      }
      case APIResourceType.PagePathnameSlice: {
        const url = new URL(`/api/makeswift/page-pathname-slices/${resourceId}`, "http://n");
        if (locale != null)
          url.searchParams.set("locale", locale);
        resource = await fetchJson(url.pathname + url.search);
        break;
      }
      case APIResourceType.Table:
        resource = await fetchJson(`/api/makeswift/tables/${resourceId}`);
        break;
      default:
        resource = null;
    }
    dispatch(apiResourceFulfilled(resourceType, resourceId, resource, locale));
    return resource;
  };
}
function defaultLocaleMiddleware(defaultLocale) {
  return () => (next) => {
    return (action) => {
      switch (action.type) {
        case ActionTypes.CHANGE_API_RESOURCE:
        case ActionTypes.EVICT_API_RESOURCE:
        case ActionTypes.SET_LOCALIZED_RESOURCE_ID: {
          const { locale } = action.payload;
          return next({
            ...action,
            payload: { ...action.payload, locale: locale ?? defaultLocale }
          });
        }
      }
      return next(action);
    };
  };
}
function configureStore({
  defaultLocale,
  serializedState
}) {
  const composeEnhancers = composeWithDevToolsDevelopmentOnly({
    name: `API client store (${(/* @__PURE__ */ new Date()).toISOString()})`,
    serialize: true,
    stateSanitizer: (state) => serializeState(state),
    actionsDenylist: [
      ActionTypes.BUILDER_POINTER_MOVE,
      ActionTypes.HANDLE_POINTER_MOVE,
      ActionTypes.ELEMENT_FROM_POINT_CHANGE
    ]
  });
  return createStore(
    reducer,
    {
      apiResources: APIResources.getInitialState(serializedState?.apiResources),
      localizedResourcesMap: LocalizedResourcesMap.getInitialState(
        serializedState?.localizedResourcesMap
      )
    },
    composeEnhancers(applyMiddleware(thunk, defaultLocaleMiddleware(defaultLocale)))
  );
}
export {
  configureStore,
  fetchAPIResource,
  getAPIResource,
  getHasAPIResource
};
//# sourceMappingURL=makeswift-api-client.js.map